import React, { useEffect, useId, useRef, useState } from 'react';
import { HelpCircle, Mic, Volume2, VolumeX, Compass } from 'lucide-react';
import type { SpineStep } from '../hooks/sessionTypes';
import { useCaptureTick } from '../hooks/useCaptureTick';
import '../styles/SpineView.css';

// SpineView — the plain-language projection of the forming process shown
// while the session is in `narrate` (002-narrate-capture, FR-006). It is
// purely presentational: it takes the spine steps and renders them as a
// semantic ordered list, with no Event Storming notation leaking through
// (SC-005).
//
// Accessibility (UX Guidelines):
//   - a polite live region wraps the list so a newly-landed step is
//     announced (present in BOTH the populated and empty states);
//   - `uncertain` steps carry a NON-COLOUR signal — a visible "to revisit"
//     label, a labelled HelpCircle icon, and data-state="uncertain" — so
//     the distinction survives for assistive tech; `unconfirmed` rows carry
//     none of those signals.
//   - the empty state is a calm "I'm listening — keep going" placeholder,
//     never a blank void.
//
// "Being-heard" feedback (003-narrate-voice-feedback, US3): because voice is
// now deliberately quieter (P2), the SCREEN carries the "noted" signal:
//   - the most-recently-added step holds a provisional, NON-reserved "latest"
//     emphasis (data-latest + ap-spine-step--latest) that decays as the next
//     step lands (FR-012/FR-013) — never teal/coral, never a confirmed marker;
//   - a running step count accrues ("N steps so far"), with the empty state
//     keeping the calm placeholder rather than a "0 steps" void (FR-015);
//   - while the mic is live (`isListening`) a labelled "listening" indicator
//     shows so silence reads as attentiveness (FR-014);
//   - an optional, mutable, default-OFF capture tick sounds as a step lands
//     (FR-016) — its VISIBLE counterpart (animate-in + latest emphasis + count)
//     works regardless of sound;
//   - the newest step's label lives inside the polite live region so every
//     landed step is announced in BOTH modalities (FR-017).
//
// Branch-aware render (004-branch-aware-spine, US2/US3): the SAME flat
// SpineStep[] now carries per-step structure fields (laneId/laneLabel/
// branchOfStepOrder/isSeparateJourney/marker/needsCheck) and a spine-level
// `overflow` flag (carried on the first step / a prop). SpineView ASSEMBLES the
// nested DOM from that flat array (plan §B/§D):
//   - a trunk forking step OWNS one nested <ol> per lane hanging off it, each
//     with an accessible group label = its laneLabel (FR-009/FR-010);
//   - separate-journey steps render OUTSIDE the trunk in a distinct labelled
//     region, never as the next trunk row (FR-009/SC-003);
//   - on overflow a quiet, non-coral "see the full map" hint shows and the
//     trunk renders truthfully — no fabricated nesting (FR-013/SC-004);
//   - a marker:'inferred' step carries a Sparkles glyph + "inferred" text +
//     data-marker hook (FR-005/FR-011); a needsCheck step a quiet "to check"
//     sub-line + a header "N to check" count beside "N steps so far" (FR-012).
// A LINEAR flow (flat {order,label,state}) collapses to a single <ol> exactly
// as today (FR-009/SC-006).

interface SpineViewProps {
  steps: SpineStep[];
  // True while the mic is live (`callStatus === 'On Call'`, threaded by the
  // shell). Drives the labelled "listening" indicator (FR-014).
  isListening?: boolean;
  // Spine-level overflow signal. Read from EITHER this prop OR the first
  // step's `overflow` field (the carrier that survives JSON serialization).
  overflow?: boolean;
}

const TRUNK_LANE_ID = 'trunk';

// A step is on the trunk when it is not a separate journey and either has no
// laneId (a bare pre-004 step) or carries the well-known trunk id. Backward-
// safe: a flat {order,label,state} step is trunk.
function isTrunkStep(step: SpineStep): boolean {
  if (step.isSeparateJourney) return false;
  return step.laneId === undefined || step.laneId === TRUNK_LANE_ID;
}

// The inner content of a step row (order chip + label + the one consolidated
// uncertainty cue). Factored out of the <li> so a forking step's <li> can
// render this content AND own its nested lane <ol>s without duplicating markup.
//
// 005-calm-spine-redesign — calm by subtraction:
//   - the dead `inferred` provenance marker is GONE (it never fired; the
//     projection pins marker:'said') (FR-001/SC-002);
//   - the two remaining uncertainty signals — the `state:'uncertain'`
//     "to revisit" flag and the `needsCheck` "to check" sub-line — are MERGED
//     into ONE quiet cue: the recessed dashed left-edge (data-state="uncertain"
//     on the <li>) PLUS at most one labelled HelpCircle, shown when the row is
//     `state==='uncertain' || needsCheck===true` (FR-002);
//   - the inline `checkReason` prose is OFF the default glance (FR-003); when a
//     `needsCheck` step carries a reason it is reachable ON DEMAND via a
//     non-modal disclosure under the row (a <button aria-expanded> toggling a
//     <p>) — never a modal, never focus theft (FR-006/SC-003). The cue also
//     carries the reason as an aria-label so assistive tech can reach it.
const SpineRowBody: React.FC<{ step: SpineStep }> = ({ step }) => {
  const uncertain = step.state === 'uncertain';
  const needsCheck = step.needsCheck === true;
  // The ONE consolidated cue fires when the model is unsure about the step —
  // whether that came from the `#uncertain` projection or the deterministic
  // structural-gap `needsCheck` signal. One cue for both (FR-002).
  const flagged = uncertain || needsCheck;
  // The reason rides on-demand only (FR-003/FR-006). Present iff this step
  // actually needs a check and carries a deterministic reason.
  const reason = needsCheck && step.checkReason ? step.checkReason : '';

  const [reasonOpen, setReasonOpen] = useState(false);
  const reasonId = useId();

  // The cue's accessible label: a calm "needs a look", extended with the reason
  // (off the glance, but reachable to assistive tech). Deliberately avoids the
  // retired "to revisit"/"to check" wording so the consolidated cue is never
  // mistaken for the old dual signals.
  const cueLabel = reason ? `Needs a look — ${reason}` : 'Needs a look';

  return (
    <>
      <span className="ap-spine-order" aria-hidden="true">
        {step.order}
      </span>
      <div className="ap-spine-body">
        <div className="ap-spine-line">
          <span className="ap-spine-label">{step.label}</span>
          {/* The ONE consolidated uncertainty cue (FR-002/FR-011): a single
              labelled HelpCircle paired with a visible word, conveyed by MORE
              than colour. Neutral tokens only — never teal/coral, never a
              confirmed/active/strong affordance. The recessed dashed left-edge
              (data-state="uncertain" on the <li>) is the quiet other half of
              the same one cue. */}
          {flagged && (
            <span className="ap-spine-cue" title={reason || undefined} aria-label={cueLabel}>
              {/* The lucide `HelpCircle` glyph. We pin an explicit
                  `lucide-help-circle` class because the lucide build renames
                  the default class across versions (e.g.
                  `lucide-circle-question-mark`); the stable class keeps the cue
                  recognisable as the one consolidated HelpCircle cue. */}
              <HelpCircle
                size={14}
                className="ap-spine-cue-icon lucide-help-circle"
                aria-hidden="true"
              />
              <span className="ap-spine-cue-text">Needs a look</span>
            </span>
          )}
        </div>
        {/* On-demand detail (FR-006/SC-003) — a small, NON-MODAL disclosure
            under the row. Collapsed by default so the reason stays OFF the
            glance; toggling reveals it WITHOUT moving focus off the spine and
            WITHOUT a dialog/backdrop. Only present when a reason exists. */}
        {reason && (
          <div className="ap-spine-detail">
            <button
              type="button"
              className="ap-spine-detail-toggle"
              aria-expanded={reasonOpen}
              aria-controls={reasonId}
              aria-label="Why this needs a look"
              onClick={() => setReasonOpen((open) => !open)}
            >
              Why?
            </button>
            {reasonOpen && (
              <p id={reasonId} className="ap-spine-detail-reason">
                {reason}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// One rendered step row (a <li>). Shared by the trunk and the nested lanes so a
// branch step row carries the same affordances (state, latest, marker, check).
const SpineRow: React.FC<{ step: SpineStep; isLatest: boolean }> = ({ step, isLatest }) => (
  <li
    className={`ap-spine-step${isLatest ? ' ap-spine-step--latest' : ''}`}
    data-state={step.state}
    // Non-reserved "latest" marker (FR-012). Additive and distinct: the row
    // stays data-state="unconfirmed" and never picks up a reserved
    // strong/confirmed/active marker.
    {...(isLatest ? { 'data-latest': 'true' } : {})}
  >
    <SpineRowBody step={step} />
  </li>
);

const SpineView: React.FC<SpineViewProps> = ({ steps, isListening, overflow }) => {
  // Render in spine order regardless of incoming order. Copy first so we
  // never mutate the caller's array.
  const ordered = [...steps].sort((a, b) => a.order - b.order);

  // The newest step is the one with the highest `order` (FR-013/FR-016) —
  // still the single newest across ALL lanes, so the latest emphasis is
  // well-defined even when the newest lands in a branch lane. Deriving it from
  // the max order keeps the marker stable across re-renders.
  const latestOrder = ordered.length ? ordered[ordered.length - 1].order : null;

  // Spine-level overflow: the prop OR the carrier on the first step (the one
  // that survives JSON over the done frame / GET). Either lights the hint.
  const hasOverflow = overflow === true || ordered[0]?.overflow === true;

  // Track the previous step count so we can fire the optional capture tick only
  // when the spine actually GREW (a step landed), not on unrelated re-renders.
  const { soundOn, toggleSound, playTick } = useCaptureTick();
  const prevCountRef = useRef(0);
  useEffect(() => {
    if (ordered.length > prevCountRef.current) {
      playTick();
    }
    prevCountRef.current = ordered.length;
  }, [ordered.length, playTick]);

  // ── Assemble the nested structure from the flat array (plan §D) ──────────
  // Trunk rows in order; branch lanes grouped by laneId and anchored to the
  // forking step's order; separate journeys broken out into their own region.
  const trunkSteps = ordered.filter(isTrunkStep);
  const separateSteps = ordered.filter((s) => s.isSeparateJourney === true);

  // Branch steps: a non-trunk, non-separate step anchored to a forking step.
  // Group by laneId, preserving order within each lane; remember the lane label
  // and which trunk step order it hangs off.
  type Lane = { laneId: string; laneLabel: string; branchOfStepOrder: number; steps: SpineStep[] };
  const lanesByFork = new Map<number, Lane[]>();
  const laneById = new Map<string, Lane>();
  for (const step of ordered) {
    if (isTrunkStep(step) || step.isSeparateJourney === true) continue;
    if (typeof step.branchOfStepOrder !== 'number') continue;
    const laneId = step.laneId ?? `lane-${step.order}`;
    let lane = laneById.get(laneId);
    if (!lane) {
      lane = {
        laneId,
        laneLabel: step.laneLabel ?? '',
        branchOfStepOrder: step.branchOfStepOrder,
        steps: [],
      };
      laneById.set(laneId, lane);
      const arr = lanesByFork.get(step.branchOfStepOrder) ?? [];
      arr.push(lane);
      lanesByFork.set(step.branchOfStepOrder, arr);
    }
    lane.steps.push(step);
  }

  const count = trunkSteps.length;
  const countLabel = `${count} ${count === 1 ? 'step' : 'steps'} so far`;
  // "N to check" (FR-012): the number of steps (across the whole spine) flagged
  // needsCheck. Absent (no element) when zero — never "0 to check".
  const toCheckCount = ordered.filter((s) => s.needsCheck === true).length;

  return (
    <div className="ap-spine" aria-live="polite">
      <div className="ap-spine-head">
        <p className="ap-spine-caption">The story so far</p>
        {isListening && (
          // Labelled live "listening" indicator (FR-014). Icon is decorative
          // beside the visible "Listening" text; the wrapper carries the
          // accessible name so it is discoverable by /listening/i.
          <span className="ap-spine-listening" aria-label="Listening">
            <Mic size={14} className="ap-spine-listening-icon" aria-hidden="true" />
            <span className="ap-spine-listening-text">Listening</span>
          </span>
        )}
      </div>

      {ordered.length === 0 ? (
        <p className="ap-spine-empty">I&rsquo;m listening — keep going.</p>
      ) : (
        <>
          <ol className="ap-spine-list">
            {trunkSteps.map((step) => {
              const lanes = lanesByFork.get(step.order) ?? [];
              const isLatest = step.order === latestOrder;
              // A non-forking trunk step is a plain row (identical to today, so
              // a LINEAR spine collapses to a single flat <ol> — SC-006).
              if (lanes.length === 0) {
                return <SpineRow key={step.order} step={step} isLatest={isLatest} />;
              }
              // A FORKING trunk step's <li> OWNS one nested <ol> per lane
              // hanging off it (FR-009/FR-010). Each nested list carries an
              // accessible group label = its lane label, via aria-label on the
              // <ol> AND a visible heading inside a role="group" wrapper, so
              // branch membership is perceivable (not by indent/colour alone).
              // The branch steps live HERE, never as direct trunk siblings.
              return (
                <li
                  key={step.order}
                  className={`ap-spine-step ap-spine-step--fork${isLatest ? ' ap-spine-step--latest' : ''}`}
                  data-state={step.state}
                  {...(isLatest ? { 'data-latest': 'true' } : {})}
                >
                  <div className="ap-spine-fork-row">
                    <SpineRowBody step={step} />
                  </div>
                  <div className="ap-spine-lanes">
                    {lanes.map((lane) => (
                      <div
                        key={lane.laneId}
                        role="group"
                        aria-label={lane.laneLabel}
                        className="ap-spine-lane"
                      >
                        <p className="ap-spine-lane-label" aria-hidden="true">
                          {lane.laneLabel}
                        </p>
                        <ol className="ap-spine-lane-list" aria-label={lane.laneLabel}>
                          {lane.steps.map((branchStep) => (
                            <SpineRow
                              key={branchStep.order}
                              step={branchStep}
                              isLatest={branchStep.order === latestOrder}
                            />
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Separate-journey region (FR-009/SC-003) — distinct, labelled, and
              OUTSIDE the trunk <ol>; never appended as the next trunk row. */}
          {separateSteps.length > 0 && (
            <section
              role="group"
              aria-label="Separate journey"
              className="ap-spine-separate"
            >
              <h3 className="ap-spine-separate-label">Separate journey</h3>
              <ol className="ap-spine-separate-list">
                {separateSteps.map((step) => (
                  <SpineRow
                    key={step.order}
                    step={step}
                    isLatest={step.order === latestOrder}
                  />
                ))}
              </ol>
            </section>
          )}

          {/* Overflow hint (FR-013/SC-004) — quiet, NEUTRAL (never coral): the
              topology exceeded what the list can draw honestly, so we point to
              the full map rather than fabricate nesting. */}
          {hasOverflow && (
            <p className="ap-spine-overflow">
              <Compass size={14} className="ap-spine-overflow-icon" aria-hidden="true" />
              <span className="ap-spine-overflow-text">
                There&rsquo;s more here than fits — see the full map.
              </span>
            </p>
          )}

          <div className="ap-spine-foot">
            {/* Running count (FR-015) — a dedicated element, not a step row. */}
            <p className="ap-spine-count">{countLabel}</p>
            {/* "N to check" count (FR-012) — beside the running count; ABSENT
                (no element) when zero, so there is never a "0 to check". The
                trailing space keeps "to check" at a word boundary even when the
                next sibling's text abuts it in textContent. */}
            {toCheckCount > 0 && (
              <p className="ap-spine-check-count">
                {toCheckCount} to check{' '}
              </p>
            )}
            {/* Capture-tick control (FR-016): OFF by default, mutable. The
                VISIBLE "heard" signal (animate-in + latest emphasis + count)
                works regardless of this preference. */}
            <button
              type="button"
              role="switch"
              aria-checked={soundOn}
              aria-label={soundOn ? 'Mute capture sound' : 'Enable capture sound'}
              className="ap-spine-tick-toggle"
              onClick={toggleSound}
            >
              {soundOn ? (
                <Volume2 size={14} className="ap-spine-tick-icon" aria-hidden="true" />
              ) : (
                <VolumeX size={14} className="ap-spine-tick-icon" aria-hidden="true" />
              )}
              <span className="ap-spine-tick-text">Sound</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SpineView;
